import { CatalogueEntry, DeployedExercise, DeployedLesson } from "routes/createContent/api/catalogueDeployments";
import { serializeSchedule } from "routes/createContent/views/templates/serializeSchedule";

const INDENT = "  ";

const orNone = (value: string | null | undefined) => (value && value.length > 0 ? value : "(none)");

const listOrNone = (values: string[]) => (values.length > 0 ? [...values].sort().join(", ") : "(none)");

const serializeExercise = (exercise: DeployedExercise): string => {
  const lines: string[] = [
    `exercise ${exercise.name}${exercise.retired ? " (retired)" : ""}`,
    `${INDENT}function: ${exercise.primary_function}`,
    `${INDENT}joints: ${listOrNone(exercise.target_joints)}`,
    `${INDENT}premium: ${exercise.premium ? "yes" : "no"}`,
    `${INDENT}duration: ${exercise.duration_minutes ? `${exercise.duration_minutes} min` : "(none)"}`,
    "",
    `levels (${exercise.levels.length})`,
  ];

  for (const level of [...exercise.levels].sort((a, b) => a.level - b.level)) {
    lines.push(`${INDENT}level ${level.level}${level.deprecated ? " (deprecated)" : ""}`);
    lines.push(`${INDENT}${INDENT}sets and reps: ${level.sets} x ${level.reps}`);
    lines.push(`${INDENT}${INDENT}equipment: ${listOrNone(level.equipment)}`);
    lines.push(`${INDENT}${INDENT}position: ${orNone(level.position)}`);
    lines.push(`${INDENT}${INDENT}movement: ${orNone(level.movement)}`);
    lines.push(`${INDENT}${INDENT}duration unit: ${orNone(level.duration_unit)}`);
    lines.push(`${INDENT}${INDENT}duration icon: ${orNone(level.duration_icon)}`);

    for (const translation of [...level.translations].sort((a, b) => a.locale.localeCompare(b.locale))) {
      lines.push(`${INDENT}${INDENT}${translation.locale}`);
      lines.push(`${INDENT}${INDENT}${INDENT}title: ${translation.title}`);
      lines.push(`${INDENT}${INDENT}${INDENT}variation: ${orNone(translation.variation_title)}`);
      lines.push(`${INDENT}${INDENT}${INDENT}about: ${orNone(translation.about)}`);
      lines.push(`${INDENT}${INDENT}${INDENT}instructions icon: ${translation.instructions_icon_name}`);
      lines.push(`${INDENT}${INDENT}${INDENT}instructions 1: ${orNone(translation.instructions_text1)}`);
      lines.push(`${INDENT}${INDENT}${INDENT}instructions 2: ${translation.instructions_text2}`);
      lines.push(`${INDENT}${INDENT}${INDENT}instructions 3: ${orNone(translation.instructions_text3)}`);
      lines.push(
        `${INDENT}${INDENT}${INDENT}tip: ${orNone(translation.tip_title)} — ${orNone(translation.tip_description)}`
      );
      lines.push(`${INDENT}${INDENT}${INDENT}image: ${orNone(translation.image_url)}`);
      lines.push(`${INDENT}${INDENT}${INDENT}video: ${orNone(translation.video_file_url ?? translation.video_url)}`);
      lines.push(`${INDENT}${INDENT}${INDENT}thumbnail: ${orNone(translation.video_thumbnail_file_url)}`);
      lines.push(`${INDENT}${INDENT}${INDENT}captions: ${orNone(translation.caption_file_url)}`);
    }
    lines.push("");
  }

  return lines.join("\n");
};

const serializeLesson = (lesson: DeployedLesson): string => {
  const lines: string[] = [
    `lesson ${lesson.name}${lesson.retired ? " (retired)" : ""}`,
    `${INDENT}joints: ${listOrNone(lesson.target_joints)}`,
    "",
    `parts (${lesson.parts.length})`,
  ];

  for (const part of [...lesson.parts].sort((a, b) => a.order - b.order)) {
    lines.push(`${INDENT}part ${part.order}${part.deprecated ? " (deprecated)" : ""}`);

    for (const translation of [...part.translations].sort((a, b) => a.locale.localeCompare(b.locale))) {
      lines.push(`${INDENT}${INDENT}${translation.locale}`);
      lines.push(`${INDENT}${INDENT}${INDENT}title: ${translation.title}`);
      lines.push(`${INDENT}${INDENT}${INDENT}image: ${orNone(translation.image_url)}`);

      for (const block of translation.content) {
        lines.push(`${INDENT}${INDENT}${INDENT}${block.type}: ${block.content}`);
      }

      for (const question of translation.questions) {
        lines.push(`${INDENT}${INDENT}${INDENT}question: ${question.title}`);
        for (const answer of question.answers) {
          lines.push(`${INDENT}${INDENT}${INDENT}${INDENT}${answer.correct ? "correct" : "wrong"}: ${answer.text}`);
        }
        lines.push(`${INDENT}${INDENT}${INDENT}${INDENT}tip: ${orNone(question.tip_description)}`);
      }
    }
    lines.push("");
  }

  return lines.join("\n");
};

export const serializeCatalogueEntry = (entry: CatalogueEntry): string => {
  if (entry.exercise) return serializeExercise(entry.exercise);
  if (entry.lesson) return serializeLesson(entry.lesson);
  if (entry.template) return serializeSchedule(entry.template);
  return "";
};
